import { useEffect, useRef, useState } from 'react';
import { Input } from 'antd';
import FormDialog from '../../../common/FormDialog';
import HFbutton from '../../../button/HFbutton';
import TextWinds from '../../../common/TextWinds';
import { Field, ReadOnlyField } from '../../../common/form/Field';
import { studentType } from '../../../../constants/types/student-type';
import { applyStudentTransferRequest } from '../../../../api/utils/lms/student/api';
import { getErrorMessages } from '../../../../api/utils/util-func';
import { useAppDispatch, useAppSelector } from '../../../../store/hook';
import { openAlert } from '../../../../store/features/modalSlice';
import { selectLmsLists } from '../../../../store/features/lmsSlice';

/** 교사 이름 입력 길이 제한 */
const TEACHER_NAME_MAX = 30;
/** 교사 아이디 입력 길이 제한 */
const TEACHER_UID_MAX = 30;
/** 신청 사유 입력 길이 제한 */
const REASON_MAX = 500;

/**
 * 서버는 교사 정보를 문자열 하나(toTeacherInfo)로 받는다.
 * 관리자가 대상 교사를 바로 찾을 수 있도록 이름/아이디를 항상 같은 형식으로 합쳐 보낸다.
 */
const formatToTeacherInfo = (name: string, uid: string) =>
  `이름: ${name.trim()} / 아이디: ${uid.trim()}`;

interface StudentTransferRequestDialogProps {
  open: boolean;
  onClose: () => void;
  /** 이관 대상 학생. null 이면 팝업이 닫힌 상태. */
  student: studentType | null;
  /** 신청 후 이관 신청 목록을 다시 불러온다. */
  reloadTransferRequests: () => void;
}

/**
 * LMS > 학생 관리 > 학생 이관 신청 모달
 *
 * 학생 한 명을 다른 선생님에게 이관해 달라고 고객센터에 신청하는 팝업.
 * 관리자가 대상 교사를 특정할 수 있도록 이름과 아이디를 따로 받고,
 * 실제 이관 대상 교사/클래스는 관리자가 처리하면서 확정한다.
 */
const StudentTransferRequestDialog = ({
  open,
  onClose,
  student,
  reloadTransferRequests,
}: StudentTransferRequestDialogProps) => {
  const dispatch = useAppDispatch();
  /** 현재 보고 있는 내 LMS 클래스. 이관 신청은 이 클래스 기준으로 접수된다. */
  const lmsLists = useAppSelector(selectLmsLists);

  const [toTeacherName, setToTeacherName] = useState('');
  const [toTeacherUid, setToTeacherUid] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** 중복 요청 방지. 화면 갱신을 기다리지 않고 즉시 막아야 하므로 state 와 별도로 둔다. */
  const submittingRef = useRef(false);

  useEffect(() => {
    // 닫았다 다시 열면 이전 입력이 남지 않도록 초기화한다.
    if (!open) return;

    setToTeacherName('');
    setToTeacherUid('');
    setReason('');
  }, [open, student]);

  /** 아이디를 이름처럼 적는 실수를 막는다. 형식 자체는 관리자가 처리하며 최종 확인한다. */
  const uidError =
    toTeacherUid && /\s/.test(toTeacherUid)
      ? '아이디에 공백을 넣을 수 없습니다.'
      : undefined;

  const canSubmit =
    !!toTeacherName.trim() &&
    !!toTeacherUid.trim() &&
    !uidError &&
    !!reason.trim();

  const submitHandler = async () => {
    if (!student || !canSubmit) return;

    // 클래스 정보를 못 받아온 상태면 어느 클래스의 신청인지 특정할 수 없다.
    if (!lmsLists) {
      dispatch(
        openAlert({
          message:
            '클래스 정보를 불러오지 못했습니다.\n페이지를 새로고침한 후 다시 시도해주세요.',
        }),
      );
      return;
    }

    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);

    try {
      await applyStudentTransferRequest({
        studentId: student.id,
        courseId: lmsLists.course.id,
        courseName: lmsLists.course.name,
        toTeacherInfo: formatToTeacherInfo(toTeacherName, toTeacherUid),
        reason: reason.trim(),
      });

      onClose();
      reloadTransferRequests();
      dispatch(
        openAlert({
          message:
            '이관 신청이 접수되었습니다.\n관리자 확인 후 처리되며, 진행 상태는 ‘이관 신청 내역’에서 확인할 수 있습니다.',
        }),
      );
    } catch (error) {
      dispatch(openAlert({ message: getErrorMessages(error) }));
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  // student 가 없으면 제목과 대상을 만들 수 없으므로 렌더하지 않는다.
  if (!open || !student) return null;

  return (
    <FormDialog
      open={open}
      title="학생 이관 신청"
      onClose={onClose}
      footer={
        <div className="flex flex-col gap-2 sm:flex-row-reverse">
          <HFbutton
            onClick={submitHandler}
            disabled={!canSubmit || isSubmitting}
          >
            {isSubmitting ? '신청 중...' : '신청'}
          </HFbutton>
          <HFbutton
            type="primary-outlined"
            onClick={onClose}
            disabled={isSubmitting}
          >
            취소
          </HFbutton>
        </div>
      }
    >
      <div className="flex flex-col">
        <ReadOnlyField label="이관 대상 학생" value={student.name} />

        <Field label="이관 받을 선생님 이름" hint="필수">
          <Input
            size="large"
            maxLength={TEACHER_NAME_MAX}
            placeholder="이관 받을 선생님의 이름 입력"
            value={toTeacherName}
            onChange={(event) => setToTeacherName(event.target.value)}
            className="rounded p-3"
          />
        </Field>

        <Field label="이관 받을 선생님 아이디" hint="필수" error={uidError}>
          <Input
            size="large"
            maxLength={TEACHER_UID_MAX}
            placeholder="이관 받을 선생님의 젭스 아이디 입력"
            value={toTeacherUid}
            onChange={(event) => setToTeacherUid(event.target.value.trim())}
            className="rounded p-3"
          />
        </Field>

        <Field label="신청 사유" hint="필수">
          <Input.TextArea
            rows={3}
            maxLength={REASON_MAX}
            showCount
            placeholder="이관이 필요한 사유를 입력해주세요."
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>

        <div className="mt-4 rounded bg-purple0 p-3">
          <TextWinds type="content_body3" color="purple5" className="mb-1">
            학생 이관 안내
          </TextWinds>
          <ul className="ml-4 list-disc space-y-1 text-purple5">
            <li className="text-xs font-normal">
              이관 시 학생의 기존 학습 기록도 함께 이전돼요.
            </li>
            <li className="text-xs font-normal">
              이관은 신청 후 관리자 확인을 거쳐 처리되며, 즉시 반영되지 않아요.
            </li>
            <li className="text-xs font-normal">
              이관 받을 선생님의 이름과 아이디를 정확하게 입력해주세요.
            </li>
            <li className="text-xs font-normal">
              신청이 완료되고 처리 대기 중인 신청은 ‘이관 신청 내역’에서 취소할
              수 있어요.
            </li>
          </ul>
        </div>
      </div>
    </FormDialog>
  );
};

export default StudentTransferRequestDialog;
